/**
 * Leakage items for one report: upload-status tabs, supplier/PO/description
 * search, commodity typeahead, sortable columns, 100/250/500 pager top and
 * bottom, and an Excel export of exactly what is filtered and sorted.
 */
import { useCallback } from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import SearchableSelect from '../../components/ui/SearchableSelect';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { ListPager, ListSearch, useListControls } from '../../components/common/listControls';
import { Loader2, AlertCircle, RefreshCw, FileSpreadsheet } from 'lucide-react';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import type { SpendLeakageItem } from '../../lib/api';
import { commodityLabel } from '../barratt/commodityNames';
import { itemCommodity, type UploadFilter, type UseLeakageItemsResult } from './useLeakageItems';

interface LeakageItemsTableProps extends UseLeakageItemsResult {
  formatCurrency: (amount: number | string) => string;
  /** Used in the export file name. */
  reportNumber?: string;
}

const EMPTY_BY_FILTER: Record<UploadFilter, string> = {
  all: 'No leakage items identified in this report',
  uploaded: 'No items from suppliers who uploaded this period',
  not_uploaded: 'No items from suppliers who did not upload this period',
};

/** Uploaded / Not uploaded / — for one line, from supplier_uploaded. */
export function UploadedChip({ value }: { value: boolean | null | undefined }) {
  if (value === true) {
    return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Uploaded</Badge>;
  }
  if (value === false) {
    return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Not uploaded</Badge>;
  }
  return <span className="text-muted-foreground">—</span>;
}

const uploadText = (v: boolean | null | undefined) => (v === true ? 'Uploaded' : v === false ? 'Not uploaded' : '');
const supplierOf = (item: SpendLeakageItem) => item.supplier_display || item.supplier_name || '';

export default function LeakageItemsTable({
  filter,
  setFilter,
  commodity,
  setCommodity,
  commodities,
  items,
  count,
  total,
  loading,
  error,
  reload,
  formatCurrency,
  reportNumber,
}: LeakageItemsTableProps) {
  const list = useListControls<SpendLeakageItem>(items, {
    searchText: (item) => [item.po_number, item.description, supplierOf(item), item.category_display, itemCommodity(item)].join(' '),
    sorters: {
      po: (item) => item.po_number || '',
      description: (item) => item.description || '',
      commodity: (item) => itemCommodity(item),
      category: (item) => item.category_display || item.category,
      supplier: (item) => supplierOf(item),
      upload: (item) => uploadText(item.supplier_uploaded),
      date: (item) => item.transaction_date,
      spend: (item) => Number(item.spend_amount) || 0,
      variance: (item) => Number(item.variance_amount) || 0,
    },
    defaultSort: { key: 'spend', dir: 'desc' },
    tieBreak: (item) => String(item.id),
    resetKeys: [filter, commodity],
  });

  const exportExcel = useCallback(() => {
    const header = ['PO Number', 'Description', 'Commodity', 'Category', 'Supplier', 'Upload', 'Date', 'Spend', 'Variance'];
    const body = list.orderedRows.map((item) => [
      item.po_number || '', item.description, itemCommodity(item), item.category_display || item.category,
      supplierOf(item), uploadText(item.supplier_uploaded), item.transaction_date,
      Number(item.spend_amount) || 0, Number(item.variance_amount) || 0,
    ]);
    const sheet = XLSX.utils.aoa_to_sheet([header, ...body]);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Leakage items');
    const parts = [reportNumber || 'spend-leakage', filter !== 'all' ? filter : '', commodity, list.search ? 'search' : ''].filter(Boolean);
    XLSX.writeFile(book, `${parts.join('-')}-items.xlsx`);
  }, [list.orderedRows, list.search, filter, commodity, reportNumber]);

  const commodityOptions = commodities.map((c) => ({ value: c.code, label: `${c.code} · ${commodityLabel(c.code)} (${c.count})` }));

  const head = (key: string, label: string, className = '') => (
    <TableHead className={`cursor-pointer select-none ${className}`} onClick={() => list.sortBy(key)} data-testid={`leakage-sort-${key}`}>
      <span className="inline-flex items-center gap-1">{label}{list.sortIcon(key)}</span>
    </TableHead>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <Tabs value={filter} onValueChange={(v) => setFilter(v as UploadFilter)}>
          <TabsList data-testid="leakage-upload-filter">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="uploaded">Uploaded</TabsTrigger>
            <TabsTrigger value="not_uploaded">Not uploaded</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <div className="md:w-72">
            <ListSearch value={list.search} onChange={list.setSearch} placeholder="Search supplier, PO, description…" testId="leakage-search" />
          </div>
          <div className="md:w-72" data-testid="leakage-commodity-filter">
            <SearchableSelect options={commodityOptions} value={commodity} onChange={setCommodity} placeholder="All commodities" />
          </div>
          <Button variant="outline" size="sm" onClick={exportExcel} disabled={list.orderedRows.length === 0} data-testid="leakage-export">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>
      {error ? (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <AlertCircle className="h-8 w-8 text-red-500" />
          <p className="text-sm text-gray-500">{error}</p>
          <Button variant="outline" size="sm" onClick={reload}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 text-gray-500" data-testid="leakage-empty">
          {commodity ? `No ${commodity} items match this filter` : EMPTY_BY_FILTER[filter]}
        </div>
      ) : list.orderedRows.length === 0 ? (
        <div className="text-center py-8 text-gray-500" data-testid="leakage-empty">No items match "{list.search}"</div>
      ) : (
        <>
          <ListPager list={list} position="top" noun="items" testIdPrefix="leakage" />
          <Table>
            <TableHeader>
              <TableRow>
                {head('po', 'PO Number')}
                {head('description', 'Description')}
                {head('commodity', 'Commodity')}
                {head('category', 'Category')}
                {head('supplier', 'Supplier')}
                {head('upload', 'Upload')}
                {head('date', 'Date')}
                {head('spend', 'Spend', 'text-right')}
                {head('variance', 'Variance', 'text-right')}
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.pageRows.map((item: SpendLeakageItem) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.po_number || '-'}</TableCell>
                  <TableCell className="whitespace-pre-wrap break-words max-w-[420px]">{item.description}</TableCell>
                  <TableCell>{itemCommodity(item) ? <span title={commodityLabel(itemCommodity(item))}>{itemCommodity(item)}</span> : '-'}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{item.category_display}</Badge>
                  </TableCell>
                  <TableCell>{supplierOf(item) || '-'}</TableCell>
                  <TableCell><UploadedChip value={item.supplier_uploaded} /></TableCell>
                  <TableCell>{format(new Date(item.transaction_date), 'dd MMM yyyy')}</TableCell>
                  <TableCell className="text-right">{formatCurrency(item.spend_amount)}</TableCell>
                  <TableCell className="text-right text-red-600">{formatCurrency(item.variance_amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <ListPager list={list} position="bottom" noun="items" testIdPrefix="leakage" />
          <div className="text-xs text-gray-500">
            {count.toLocaleString()} of {total.toLocaleString()} items match the current filters
          </div>
        </>
      )}
    </div>
  );
}
